import React, { useEffect, useContext } from 'react';
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import CloseIcon from '@material-ui/icons/Close';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Box, Button, Typography } from '@material-ui/core';

import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useIsMobile } from '../../../../util/style-utils';
import BottomDrawerLayout, {
  DrawerSnapType,
} from '../bottom-drawer-layout/BottomDrawerLayout';
import OutageMapFAQAccordion from './outageMapFAQAccordion';
import OutageFAQAccordianDetailsQuery from './outageFAQAccordian.query';
import useGTMUtil from '../../../../lib/useGTMUtil';
import { LanguageContext } from '../../../../providers/LanguageProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modalWrapper: {
      display: 'flex',
      width: '60.57vw',
      maxHeight: '90.88vh',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      padding: '24px 24px 14px 24px',
      backgroundColor: colors.modalBackgroundColor,
      overflow: 'auto',
      borderRadius: '5px',
      [theme.breakpoints.down('sm')]: {
        top: '50%',
        width: '90%',
        maxHeight: '90%',
      },
    },
    closeButtonIcon: {
      position: 'absolute',
      top: 6,
      right: 24,
    },
    bottomSheet: {
      padding: theme.spacing(2.5),
      [theme.breakpoints.down('sm')]: {
        padding: 0,
        paddingBottom: 30,
      },
    },
    closeButtonWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingTop: theme.spacing(3),
    },
    closeButtonEnd: {
      background: colors.sparkBlue,
      padding: '13px 39px',
      borderRadius: 5,
      '&.MuiButton-root:hover': {
        background: colors.sparkBlue,
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        background: colors.orange,
        borderRadius: 50,
        '&.MuiButton-root:hover': {
          background: colors.sparkBlue,
        },
      },
    },
    closeButtonText: {
      lineHeight: '1.125rem',
      letterSpacing: '.36px',
      color: colors.white,
    },
    closeIcon: {
      fontSize: '1.25rem',
      color: colors.noirBlur,
    },
  }),
);

type Props = {
  open: boolean;
  onClose: () => void;
};

const outageMapFaqModal: React.FC<Props> = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { language } = useContext(LanguageContext);
  const isMobile = useIsMobile();

  const { allContentfulModuleAccordion } = OutageFAQAccordianDetailsQuery();
  const { postGTMModalVisibilityData } = useGTMUtil();

  //Push modal visibility status to the GTM
  useEffect(() => {
    const GTMPageData = {
      page: 'Outages FAQ',
      siteSection: 'Outages FAQ Modal',
    };

    postGTMModalVisibilityData({ ...GTMPageData, isVisible: true });

    return () =>
      postGTMModalVisibilityData({ ...GTMPageData, isVisible: false });
  }, []);

  const contentfulModuleAccordion = allContentfulModuleAccordion.nodes.filter(
    node => node.node_locale === language,
  )[0];

  return isMobile ? (
    <BottomDrawerLayout
      onClose={onClose}
      data-testid={'notification-options'}
      refId={8}
      defaultSnap={DrawerSnapType.default}
    >
      <Box component="div" className={classes.bottomSheet}>
        <OutageMapFAQAccordion
          accordionItems={contentfulModuleAccordion?.accordionItems}
          accordionHeader={contentfulModuleAccordion?.accordionHeader}
          isMobile={isMobile}
        />
        <Box className={classes.closeButtonWrapper}>
          <Button
            size={'small'}
            onClick={onClose}
            className={classes.closeButtonEnd}
          >
            <Typography variant={'h4'} className={classes.closeButtonText}>
              {t('CLOSE')}
            </Typography>
          </Button>
        </Box>
      </Box>
    </BottomDrawerLayout>
  ) : (
    <Modal
      aria-labelledby="report-outage-modal"
      aria-describedby="report-outage-modal"
      open={open}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <div className={classes.modalWrapper}>
        <div className={classes.closeButtonIcon}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            data-testid="close-icon"
          >
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
        <OutageMapFAQAccordion
          accordionItems={contentfulModuleAccordion?.accordionItems}
          accordionHeader={contentfulModuleAccordion?.accordionHeader}
          isMobile={isMobile}
        />
      </div>
    </Modal>
  );
};

export default outageMapFaqModal;
